<template>
  <div>
    <v-container fluid class="pa-0 pb-8">
      <v-container class="">
        <Breadcrumbs
          :title="$t('modules.conciliation.create.breadcrumbs.title')"
          :description="
            $t('modules.conciliation.create.breadcrumbs.description')
          "
        ></Breadcrumbs>
        <template v-if="!isProcessed">
          <v-col cols="5">
            <v-card outlined v-if="hasIdReviewedItems">
              <v-list class="files-list">
                <v-list-item
                  v-for="(item, i) in identificationDocsGiven"
                  :key="i"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ item.filename }}</v-list-item-title>
                  </v-list-item-content>

                  <div class="d-flex align-center">
                    <v-icon class="mr-2" :class="item.statusClass">{{
                      item.statusIcon
                    }}</v-icon>
                    {{ item.statusText }}
                  </div>
                </v-list-item>
              </v-list>
            </v-card>
            <FileUploader
              formatsAccepted=".xlsx, .xls, .xlt"
              :title="$t('modules.conciliation.create.title')"
              :subtitle="$t('modules.conciliation.create.subtitle')"
              @action="process"
              :files.sync="identificationDocs"
              v-if="showIdentificationDocs"
            />
          </v-col>
        </template>
        <template v-else-if="loading">
          <div class="text-center">
            <v-progress-circular
              :size="90"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
        </template>
        <template v-else>
          <v-row justify="center">
            <v-col sm="12" class="viewSpaces">
              <v-card outlined class="pa-5" max-width="400px">
                <h4 class="">
                  {{
                    $t("modules.conciliation.create.cardDescription.document")
                  }}
                </h4>
                <div class="d-flex justify-space-between mt-3 mb-3">
                  <div class="subtitle font-weight-bold">
                    {{
                      identificationDocs[0] ? identificationDocs[0].name : "0"
                    }}
                  </div>
                  <div>
                    <v-icon class="mr-2" color="green">mdi-check-circle</v-icon
                    >{{ $t("modules.conciliation.create.processed") }}
                  </div>
                </div>
                <div class="d-flex">
                  <v-icon class="mr-2" size="19" color="green"
                    >mdi-check-circle-outline</v-icon
                  >
                  <div class="caption text--muted mt-1">
                    {{ processedItems.length ? processedItems.length : "0" }}
                    {{
                      processedItems.length !== 1
                        ? $t(
                            "modules.conciliation.create.cardDescription.rowsProccesed"
                          )
                        : $t(
                            "modules.conciliation.create.cardDescription.rowProccesed"
                          )
                    }}
                  </div>
                </div>
                <div class="d-flex">
                  <v-icon class="mr-2" size="19" color="red"
                    >mdi-alert-circle-outline</v-icon
                  >
                  <div class="caption text--muted mt-1">
                    {{
                      noProcessedItems.length ? noProcessedItems.length : "0"
                    }}
                    {{
                      noProcessedItems.length !== 1
                        ? $t(
                            "modules.conciliation.create.cardDescription.errorsProccesed"
                          )
                        : $t(
                            "modules.conciliation.create.cardDescription.errorProccesed"
                          )
                    }}
                  </div>
                </div>
              </v-card>
              <br />
              <h4 class="mb-2">
                {{ $t("modules.conciliation.create.procesedSection.title") }}
              </h4>
              <v-card outlined class="pa-1">
                <v-data-table
                  :headers="headers"
                  :items="processedItems"
                  calculate-widths
                  hide-default-footer
                  disable-pagination
                  class="elevation-0"
                >
                 <template v-slot:item.hour="{ item }">
                    <div dark>
                      {{ item.hour }} hs
                    </div> </template
                  >
                  <template v-slot:item.credit="{ item }">
                    <div :class="getColor(item.credit)" dark>
                      {{ getText(item.credit) }}
                    </div> </template
                  >'
                  <template v-slot:item.debit="{ item }">
                    <div :class="getColor(item.debit)" dark>
                      {{ getText(item.debit) }}
                    </div>
                  </template>
                  <template v-slot:item.quantity="{ item }">
                    <div :class="getColor(item.quantity)" dark>
                      {{ getText(item.quantity) }}
                    </div>
                  </template>
                  <template v-slot:item.tariff="{ item }">
                    <div :class="getColor(item.tariff)" dark>
                      {{ getText(item.tariff) }}
                    </div>
                  </template>
                </v-data-table>
              </v-card>
              <br />
              <h4 class="mb-2">
                {{ $t("modules.conciliation.create.unprocesedSection.title") }}
              </h4>
              <v-card outlined class="pa-1">
                <v-data-table
                  :headers="headers"
                  :items="noProcessedItems"
                  calculate-widths
                  hide-default-footer
                  disable-pagination
                  class="elevation-0"
                >
                  <template v-slot:item.date="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.date) }}
                    </div> </template
                  >
                  <template v-slot:item.hour="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.hour) }} hs
                    </div> </template
                  >
                  <template v-slot:item.iddate="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.iddate) }}
                    </div> </template
                  >
                  <template v-slot:item.referenceNumber="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.referenceNumber) }}
                    </div>
                  </template>
                  <template v-slot:item.description="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.description) }}
                    </div>
                  </template>
                  <template v-slot:item.credit="{ item }">
                    <div class="red--text" dark>{{ getText(item.credit) }}</div>
                  </template>
                  <template v-slot:item.debit="{ item }">
                    <div class="red--text" dark>{{ getText(item.debit) }}</div>
                  </template>
                  <template v-slot:item.quantity="{ item }">
                    <div class="red--text" dark>
                      {{ getText(item.quantity) }}
                    </div>
                  </template>
                  <template v-slot:item.tariff="{ item }">
                    <div class="red--text" dark>{{ getText(item.tariff) }}</div>
                  </template>
                  <template v-slot:item.method="{ item }">
                    <div class="red--text" dark>{{ getText(item.method) }}</div>
                  </template>
                </v-data-table>
              </v-card>
              <br />
              <div class="d-flex justify-end">
                <Button
                  outlined
                  white
                  :text="$t('modules.conciliation.create.buttonDelete')"
                  class="mr-4 primary--text"
                  @clicked="deleteDocument"
                ></Button>

                <Button
                  :text="$t('modules.conciliation.create.buttonAccept')"
                  :errorText="'Error'"
                  @clicked="redirect"
                ></Button>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import FileUploader from '@/components/FileUploader/FileUploader';
import { $conciliations } from '../Services';
import { mapGetters } from 'vuex';
import moment from 'moment';
import baseConf from '@/constants/uri.js';
export default {
  name: 'CareersCreate',
  components: {
    Breadcrumbs,
    Button,
    FileUploader
  },
  data() {
    return {
      hasIdReviewedItems: null,
      identificationDocs: [],
      showIdentificationDocs: [],
      isProcessed: false,
      conciliationItem: [],
      processedItems: [],
      noProcessedItems: [],
      loading: false,
      headers: [
        {
          text: 'Fecha',
          class: 'primary--text ',
          width: '125px',
          value: 'date'
        },
        { text: 'Hora', class: 'primary--text', value: 'hour' },
        { text: 'ID', class: 'primary--text', value: 'iddate' },
        {
          text: 'N° Referencia',
          class: 'primary--text',
          value: 'referenceNumber'
        },
        { text: 'Descripción', class: 'primary--text', value: 'description' },
        { text: 'Crédito Neto', class: 'primary--text', value: 'credit' },
        {
          text: 'Débito Neto',
          class: 'primary--text',
          width: '125px',
          value: 'debit'
        },
        { text: 'Cantidad bruta', class: 'primary--text', value: 'quantity' },
        { text: 'Tarifa MP', class: 'primary--text', value: 'tariff' },
        { text: 'Método de pago', class: 'primary--text', value: 'method' }
      ],
      items: []
    };
  },
  computed: {
    ...mapGetters(['commons/getUserData', 'commons/getUserID'])
  },
  methods: {
    process(event) {
      this.isProcessed = true;
      this.identificationDocs = event;
      this.sendDocumentation();
    },
    async sendDocumentation() {
      this.loading = true;
      const user = this['commons/getUserData'].userId;
      const formData = new FormData();
      formData.append('userId', user);
      formData.append('file', this.identificationDocs[0]);

      const token = localStorage.getItem('access_token')
        ? JSON.parse(localStorage.getItem('access_token'))
        : '';
      const options = {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      };

      try {
        let repsonse = null;
        repsonse = await fetch(
          `${baseConf}/payment/conciliations/upload`,
          options
        );
        this.conciliationItem = await repsonse.json();
        this.processedItems = this.conciliationItem.processed
          ? this.conciliationItem.processed.map(conciliation => {
              return {
                date: conciliation.date
                  ? moment(conciliation.date)
                      .add(1, 'days')
                      .format('YYYY-MM-DD')
                  : ' ',
                hour: conciliation.date
                  ? moment(conciliation.date).format('HH:MM')
                  : ' ',
                iddate: conciliation.id ? conciliation.id : ' ',
                referenceNumber: conciliation.externalReference
                  ? conciliation.externalReference
                  : ' ',
                description: conciliation.description
                  ? conciliation.description
                  : ' ',
                credit: conciliation.netCreditAmount
                  ? conciliation.netCreditAmount
                  : ' ',
                debit: conciliation.netDebitAmount
                  ? conciliation.netDebitAmount
                  : ' ',
                quantity: conciliation.grossAmount
                  ? conciliation.grossAmount
                  : ' ',
                tariff: conciliation.mpFeeAmount
                  ? conciliation.mpFeeAmount
                  : ' ',
                method: conciliation.paymentMethod
                  ? conciliation.paymentMethod
                  : ' '
              };
            })
          : [];
        this.noProcessedItems = this.conciliationItem.notProcessed
          ? this.conciliationItem.notProcessed.map(conciliation => {
              return {
                date: conciliation.date
                  ? moment(conciliation.date)
                      .add(1, 'days')
                      .format('YYYY-MM-DD')
                  : ' ',
                hour: conciliation.date
                  ? moment(conciliation.date).format('HH:MM')
                  : ' ',
                iddate: conciliation.id ? conciliation.id : ' ',
                referenceNumber: conciliation.externalReference
                  ? conciliation.externalReference
                  : ' ',
                description: conciliation.description
                  ? conciliation.description
                  : ' ',
                credit: conciliation.netCreditAmount
                  ? conciliation.netCreditAmount
                  : ' ',
                debit: conciliation.netDebitAmount
                  ? conciliation.netDebitAmount
                  : ' ',
                quantity: conciliation.grossAmount
                  ? conciliation.grossAmount
                  : ' ',
                tariff: conciliation.mpFeeAmount
                  ? conciliation.mpFeeAmount
                  : ' ',
                method: conciliation.paymentMethod
                  ? conciliation.paymentMethod
                  : ' '
              };
            })
          : [];
      } catch (error) {
        this.loading = false;
        throw error
      } finally {
        this.loading = false;
      }
    },
    deleteDocument() {
      const info = this.processedItems.length
        ? this.processedItems[0].date
        : this.noProcessedItems
        ? this.noProcessedItems[0].date
        : null;
      const date = moment(info)
        .add(1, 'days')
        .format('YYYY/MM/DD');

      this.$store.dispatch('commons/openPopUp', {
        title: '¿Esta seguro de eliminar?',
        content: [{ value: this.identificationDocs[0].name }],
        actionPrimary: {
          text: 'Aceptar',
          callback: () => this.deleteConciliation(date)
        },
        actionSecondary: { text: 'Cancelar', callback() {} },
        icon: { color: 'error', name: 'mdi-delete' },
        color: 'primary'
      });
    },
    async deleteConciliation(date) {
      try {
        await $conciliations.delete(`?date=${date}`);
        this.redirect();
      } catch (error) {
        this.$store.dispatch('commons/openPopUp', {
          title: 'Error',
          content: [{ value: this.$t('modules.discounts.table.delete_error') }],
          actionPrimary: { text: this.$t('actions.accept'), callback() {} },
          icon: { color: 'error', name: 'mdi-close' },
          color: 'primary'
        });
        throw error;
      }
    },
    getColor(value) {
      if (value < 0) return 'red--text';
      else return value;
    },
    getText(value) {
      if (value < 0) return `( ${value} )`;
      else return value;
    },
    redirect() {
      this.$router.push('/conciliations');
    }
  },
  mounted() {}
};
</script>

<style lang='sass'>

.header-line
  border-bottom: 1px solid rgba(0, 0, 0, 0.15)

.text--muted
  color: rgba(0, 0, 0, 0.35)

.check-icon
  background: #dae2f4

</style>